import axiosInstance from './axiosInstance';
import { CreateQuestionDto, UpdateQuestionDto, QuestionDto } from '../types/questions';

// Keep backward-compatible type re-exports for components importing from this module
export type { QuestionDto as QuestionDto };

// Legacy helpers kept for existing components
export async function fetchPersonalQuestions(language?: string): Promise<QuestionDto[]> {
  const res = await axiosInstance.get<QuestionDto[]>('/questions/personal', { params: { language } });
  return res.data;
}

export async function fetchQuestionsByProfession(professionKeys: string[], language?: string): Promise<QuestionDto[]> {
  const all: QuestionDto[] = [];
  for (const key of professionKeys) {
    const res = await axiosInstance.get<QuestionDto[]>(`/questions/by-profession/${encodeURIComponent(key)}`, { params: { language } });
    all.push(...res.data);
  }
  const dedup = new Map<string, QuestionDto>();
  for (const q of all) if (!dedup.has(q.questionKey)) dedup.set(q.questionKey, q);
  return Array.from(dedup.values());
}

// Primary API used across the app
export const questionsApi = {
  getQuestions: async (filters?: {
    professionKey?: string;
    language?: string;
    questionType?: string;
    questionId?: number;
  }) => {
    const response = await axiosInstance.get<QuestionDto[]>('/questions', { params: filters });
    return response.data;
  },

  getQuestionsByProfession: async (professionKey: string, language?: string) => {
    const response = await axiosInstance.get<QuestionDto[]>(`/questions/by-profession/${professionKey}`, {
      params: { language }
    });
    return response.data;
  },

  getPersonalQuestions: async (language?: string) => {
    const response = await axiosInstance.get<QuestionDto[]>('/questions/personal', {
      params: { language }
    });
    return response.data;
  },

  createQuestion: async (data: CreateQuestionDto) => {
    const response = await axiosInstance.post<QuestionDto>('/questions', data);
    return response.data;
  },

  updateQuestion: async (id: number, data: UpdateQuestionDto) => {
    const response = await axiosInstance.put<QuestionDto>(`/questions/${id}`, data);
    return response.data;
  },

  updateQuestions: async (filters: {
    professionKey?: string;
    language?: string;
    questionType?: string;
    questionId?: number;
  }, data: UpdateQuestionDto) => {
    const response = await axiosInstance.put<QuestionDto[]>('/questions', data, { params: filters });
    return response.data;
  },

  deleteQuestion: async (id: number) => {
    const response = await axiosInstance.delete(`/questions/${id}`);
    return response.data;
  },

  deleteQuestions: async (filters: {
    professionKey?: string;
    language?: string;
    questionType?: string;
  }) => {
    const response = await axiosInstance.delete('/questions', { params: filters });
    return response.data;
  },

  reorderQuestions: async (data: {
    professionKey?: string;
    language: string;
    questionType: string;
    questionOrders: { id: number; questionNumber: number }[];
  }) => {
    const response = await axiosInstance.patch<QuestionDto[]>('/questions/reorder', data);
    return response.data;
  },
};

// Optional utilities for future filter UIs (kept from previous implementation)
export interface FilterableQuestionOption { value: string | number; label: string }
export interface FilterableQuestion {
  questionDataKey: string;
  questionText: string;
  answerType: any;
  options?: FilterableQuestionOption[];
  listItemSchema?: any[];
  hasDynamicOptions?: boolean;
  dynamicOptionSourceKey?: string;
}

export const getFilterableQuestionsByProfession = async (professionKey: string): Promise<FilterableQuestion[]> => {
  try {
    const response = await axiosInstance.get(`/questions/filterable-by-profession`, { params: { professionKey } });
    return response.data as FilterableQuestion[];
  } catch (error) {
    console.error(`Error fetching filterable questions for profession ${professionKey}:`, error);
    return [];
  }
};

export const getDistinctAnswerOptions = async (sourceKey: string, searchQuery: string): Promise<FilterableQuestionOption[]> => {
  try {
    const response = await axiosInstance.get(`/questions/distinct-answers`, { params: { sourceKey, search: searchQuery } });
    if (Array.isArray(response.data)) {
      return response.data.map((item: any) => ({
        value: item.value !== undefined ? String(item.value) : String(item),
        label: item.label !== undefined ? String(item.label) : String(item),
      }));
    }
    return [];
  } catch (error) {
    console.error(`Error fetching distinct answer options for ${sourceKey} with query "${searchQuery}":`, error);
    return [];
  }
};
